/**
 * `/oauth/rudderstack` — the landing page the webapp's `/oauth` trampoline returns a RudderStack
 * account OAuth flow to (rudder-specs lookout-audiences-initiative/activations/account-management §4.2).
 *
 * Public and stateless: it reads the provider's callback query, hands it to the opener over a
 * BroadcastChannel (origin-scoped — the only relay that survives a provider's COOP swap, which also
 * nulls `window.opener`), and closes itself. It never calls the API; the opener does, with the nonce
 * it minted, so a crafted link to this page cannot complete anyone's flow.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { GatewayBrand, GatewayShell } from '@/_shared/components/GatewayShell';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

/** One channel name, shared with `useRudderstackOAuth` — the opener listens here. */
export const RUDDERSTACK_OAUTH_CHANNEL = 'rudderstack_oauth';
export const RUDDERSTACK_OAUTH_MESSAGE_TYPE = 'rudderstack_oauth';

export interface RudderstackOauthMessage {
  type: typeof RUDDERSTACK_OAUTH_MESSAGE_TYPE;
  payload: { code: string | null; state: string | null; error: string | null; search: string };
}

const CLOSE_AFTER_MS = 50;
const STILL_OPEN_AFTER_MS = 500;

export function RudderstackOauthLanding(): React.ReactElement {
  usePageTitle('Connecting account');
  const { search } = useLocation();
  const [stillOpen, setStillOpen] = useState(false);

  // The relay runs exactly once per landing. The guard is what makes scrubbing the URL below safe:
  // without it a re-run would post a SECOND, code-less message on the same channel.
  const relayedRef = useRef(false);

  useEffect(() => {
    if (relayedRef.current) return;
    relayedRef.current = true;
    const params = new URLSearchParams(search);
    const message: RudderstackOauthMessage = {
      type: RUDDERSTACK_OAUTH_MESSAGE_TYPE,
      payload: { code: params.get('code'), state: params.get('state'), error: params.get('error'), search },
    };
    const channel = new BroadcastChannel(RUDDERSTACK_OAUTH_CHANNEL);
    channel.postMessage(message);
    channel.close();

    // Leo #9: the provider's authorization `code` must not linger in the address bar, in session
    // history, or in a Referer this page might send. It has been handed to the opener above and is
    // needed nowhere else here. Scrubbed through the raw History API rather than the router so React
    // Router's own location — and therefore this effect's `search` dependency — does not change; the
    // guard above makes that belt-and-braces rather than load-bearing.
    window.history.replaceState(null, '', window.location.pathname);

    const closeTimer = window.setTimeout(() => {
      window.close();
    }, CLOSE_AFTER_MS);
    const stillOpenTimer = window.setTimeout(() => setStillOpen(true), STILL_OPEN_AFTER_MS);
    return () => {
      window.clearTimeout(closeTimer);
      window.clearTimeout(stillOpenTimer);
    };
  }, [search]);

  return (
    <GatewayShell>
      <GatewayBrand poweredBy />
      <p className="text-sm text-muted-foreground" role="status">
        {stillOpen ? 'You can close this window.' : 'Finishing up…'}
      </p>
    </GatewayShell>
  );
}
